import { useQuery } from "@tanstack/react-query";

import { HotelBookingSuccessWhatsAppBanner } from "@/components/WhatsApp/WhatsAppBanners";
import { reservationQueryOptions } from "@/features/bookings/hooks/useReservations";
import { BookedHotelCard } from "@/features/hotels/components/hotel/BookedHotelCard";
import { HotelBookingSuccessSkeleton } from "@/features/hotels/components/hotel/HotelBookingSuccessSkeleton";
import { HotelBookingUpsellCards } from "@/features/hotels/components/hotel/HotelBookingUpsellCards";
import { HotelMapDirections } from "@/features/hotels/components/hotel/HotelMapDirections";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelTravellersSelectionContext } from "@/features/hotels/contexts/HotelTravellersSelectionContext";
import type { HotelData } from "@/features/hotels/types/types";
import { useOrder } from "@/hooks/useOrder";

export const HotelBookingSuccess = ({
  orderId,
  hotel,
}: {
  orderId: string;
  hotel: HotelData;
}) => {
  const { searchData } = useHotelSearchDataContext();
  const { selectedTravellers, leadTravellerId } =
    useHotelTravellersSelectionContext();
  const { data: orderData, isLoading: isOrderLoading } = useOrder(orderId);
  const reservationId = orderData?.reservations?.find(
    (reservation) => reservation.service === "hotel",
  )?.id;
  const { data: reservationData, isLoading: isReservationLoading } = useQuery(
    reservationQueryOptions(reservationId),
  );

  const isLoading =
    isOrderLoading ||
    !orderData?.reservations.length ||
    isReservationLoading ||
    !reservationData?.hotelReservation ||
    reservationData.hotelReservation.status === "IN_PROGRESS";

  if (isLoading) {
    return (
      <div className="px-main">
        <HotelBookingSuccessSkeleton />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5 px-main">
      <BookedHotelCard
        reservation={reservationData.hotelReservation.externalReservation}
        hotel={hotel}
        travellers={selectedTravellers}
        leadTravellerId={leadTravellerId}
        orderData={orderData}
      />
      <HotelBookingUpsellCards
        reservation={reservationData.hotelReservation.externalReservation}
        destination={searchData?.destination}
      />
      <HotelMapDirections hotel={hotel} />
      <HotelBookingSuccessWhatsAppBanner />
    </div>
  );
};
